/**
 * Spawn Monitor view: real-time monitoring dashboard for orchestrator/spawn mode.
 * Shows active spawned sub-agents, recent completions, performance stats, and live events.
 */
import { html, nothing } from "lit";
import type {
  SubagentActiveEntry,
  SubagentRecentEntry,
  SubagentsStatusResult,
} from "../controllers/subagents-status.ts";
import { t } from "../i18n.ts";

export type SpawnLifecycleEvent = {
  ts: number;
  type: "spawned" | "started" | "completed";
  runId: string;
  childSessionKey: string;
  label?: string;
  task?: string;
  model?: string;
  status?: string;
  runtimeMs?: number;
};

export type SpawnMonitorProps = {
  loading: boolean;
  error: string | null;
  status: SubagentsStatusResult | null;
  events: SpawnLifecycleEvent[];
  autoRefresh: boolean;
  onRefresh: () => void;
  onToggleAutoRefresh: () => void;
  onClearEvents: () => void;
};

function isSpawnLabel(label?: string): boolean {
  return typeof label === "string" && label.startsWith("spawn:");
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.round((ms % 60_000) / 1000);
  return `${mins}m${secs}s`;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function statusColor(status: string): string {
  switch (status) {
    case "ok":
    case "running":
      return "var(--success, #34d399)";
    case "error":
      return "var(--danger, #f87171)";
    case "timeout":
      return "var(--warning, #fbbf24)";
    default:
      return "var(--text-muted, #888)";
  }
}

function statusBadge(status: string) {
  return html`<span style="
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    padding: 1px 8px;
    border-radius: 10px;
    background: ${statusColor(status)}20;
    color: ${statusColor(status)};
  ">${status}</span>`;
}

function renderStatsCards(status: SubagentsStatusResult) {
  const spawnActive = status.active.filter((r) => isSpawnLabel(r.label));
  const spawnRecent = status.recent.filter((r) => isSpawnLabel(r.label));
  const avgRuntimeMs =
    spawnRecent.length > 0
      ? spawnRecent.reduce((sum, r) => sum + r.runtimeMs, 0) / spawnRecent.length
      : 0;
  const successCount = spawnRecent.filter((r) => r.status === "ok").length;
  const errorCount = spawnRecent.filter(
    (r) => r.status === "error" || r.status === "timeout",
  ).length;
  const successRate = spawnRecent.length > 0 ? (successCount / spawnRecent.length) * 100 : 0;

  const cards = [
    {
      label: t("spawn_monitor.active_now"),
      value: String(spawnActive.length),
      color: spawnActive.length > 0 ? "var(--accent, #6c8cff)" : "var(--text-muted, #888)",
    },
    {
      label: t("spawn_monitor.total_recent"),
      value: String(spawnRecent.length),
      color: "var(--text-muted, #ccc)",
    },
    {
      label: t("spawn_monitor.avg_runtime"),
      value: avgRuntimeMs > 0 ? formatMs(avgRuntimeMs) : "--",
      color: "var(--text-muted, #ccc)",
    },
    {
      label: t("spawn_monitor.success_rate"),
      value: spawnRecent.length > 0 ? `${Math.round(successRate)}%` : "--",
      color:
        successRate >= 90
          ? "var(--success, #34d399)"
          : successRate >= 70
            ? "var(--warning, #fbbf24)"
            : errorCount > 0
              ? "var(--danger, #f87171)"
              : "var(--text-muted, #888)",
    },
  ];

  return html`
    <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 12px; margin-bottom: 16px;">
      ${cards.map(
        (card) => html`
        <div style="
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: var(--radius-sm, 8px);
          padding: 14px 16px;
          text-align: center;
        ">
          <div style="font-size: 24px; font-weight: 700; color: ${card.color}; margin-bottom: 4px;">
            ${card.value}
          </div>
          <div style="font-size: 11px; color: var(--text-muted, #888); text-transform: uppercase; letter-spacing: 0.5px;">
            ${card.label}
          </div>
        </div>
      `,
      )}
    </div>
  `;
}

function renderActiveSpawns(active: SubagentActiveEntry[]) {
  const spawns = active.filter((r) => isSpawnLabel(r.label));
  if (spawns.length === 0) {
    return html`
      <section class="card" style="margin-bottom: 16px;">
        <div class="card-title">${t("spawn_monitor.active_title")}</div>
        <div class="muted" style="padding: 12px 0;">${t("spawn_monitor.no_active")}</div>
      </section>
    `;
  }

  return html`
    <section class="card" style="margin-bottom: 16px;">
      <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
        ${t("spawn_monitor.active_title")}
        <span style="
          background: rgba(108,140,255,0.15);
          color: var(--accent, #6c8cff);
          font-size: 11px;
          font-weight: 600;
          padding: 1px 8px;
          border-radius: 10px;
        ">${spawns.length}</span>
      </div>

      <div style="margin-top: 12px; display: flex; flex-direction: column; gap: 8px;">
        ${spawns.map(
          (run) => html`
          <div style="
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(108,140,255,0.15);
            border-radius: var(--radius-sm, 8px);
            padding: 12px 16px;
            animation: fadeIn 0.3s ease-in;
          ">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
              ${statusBadge("running")}
              <span style="font-weight: 600; font-size: 13px;">
                ${(run.label ?? "").replace(/^spawn:/, "")}
              </span>
              ${
                run.model
                  ? html`<span style="font-size: 11px; color: var(--text-muted, #888); font-family: monospace;">${run.model}</span>`
                  : nothing
              }
            </div>
            <div style="font-size: 12px; color: var(--text-muted, #aaa); margin-bottom: 4px;">
              ${run.task.length > 150 ? run.task.slice(0, 150) + "..." : run.task}
            </div>
            <div style="display: flex; gap: 12px; font-size: 11px; color: var(--text-muted, #888);">
              <span>${t("spawn_monitor.elapsed")}: ${formatMs(run.runtimeMs)}</span>
              <span style="font-family: monospace;">${run.childSessionKey.slice(0, 24)}</span>
            </div>
          </div>
        `,
        )}
      </div>
    </section>
  `;
}

function renderRecentTable(recent: SubagentRecentEntry[]) {
  const spawns = recent.filter((r) => isSpawnLabel(r.label));
  return html`
    <section class="card" style="margin-bottom: 16px;">
      <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
        ${t("spawn_monitor.recent_title")}
        ${
          spawns.length > 0
            ? html`<span style="
              background: rgba(255,255,255,0.08);
              color: var(--text-muted, #888);
              font-size: 11px;
              font-weight: 600;
              padding: 1px 8px;
              border-radius: 10px;
            ">${spawns.length}</span>`
            : nothing
        }
      </div>
      <div class="card-sub">${t("spawn_monitor.recent_sub")}</div>

      ${
        spawns.length === 0
          ? html`<div class="muted" style="padding: 12px 0;">${t("spawn_monitor.no_recent")}</div>`
          : html`
            <div style="margin-top: 12px; overflow-x: auto;">
              <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                <thead>
                  <tr style="border-bottom: 1px solid rgba(255,255,255,0.08);">
                    <th style="text-align: left; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("spawn_monitor.col_message")}</th>
                    <th style="text-align: left; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("spawn_monitor.col_status")}</th>
                    <th style="text-align: left; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("spawn_monitor.col_model")}</th>
                    <th style="text-align: right; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("spawn_monitor.col_runtime")}</th>
                    <th style="text-align: right; padding: 6px 12px; color: var(--text-muted, #888); font-weight: 500; font-size: 12px;">${t("spawn_monitor.col_time")}</th>
                  </tr>
                </thead>
                <tbody>
                  ${spawns.map(
                    (run) => html`
                    <tr style="border-bottom: 1px solid rgba(255,255,255,0.04);">
                      <td style="padding: 8px 12px;">
                        <div style="font-weight: 500;">${(run.label ?? "").replace(/^spawn:/, "") || run.runId.slice(0, 12)}</div>
                        <div style="font-size: 11px; color: var(--text-muted, #888); max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                          ${run.task}
                        </div>
                      </td>
                      <td style="padding: 8px 12px;">${statusBadge(run.status)}</td>
                      <td style="padding: 8px 12px;">
                        <span style="font-family: monospace; font-size: 12px; color: var(--text-muted, #aaa);">
                          ${run.model || "--"}
                        </span>
                      </td>
                      <td style="padding: 8px 12px; text-align: right; font-family: monospace; font-size: 12px;">
                        ${formatMs(run.runtimeMs)}
                      </td>
                      <td style="padding: 8px 12px; text-align: right; font-size: 12px; color: var(--text-muted, #888);">
                        ${run.endedAt ? formatTime(run.endedAt) : "--"}
                      </td>
                    </tr>
                  `,
                  )}
                </tbody>
              </table>
            </div>
          `
      }
    </section>
  `;
}

function renderLiveEvents(events: SpawnLifecycleEvent[], onClear: () => void) {
  if (events.length === 0) {
    return html`
      <section class="card">
        <div class="card-title">${t("spawn_monitor.events_title")}</div>
        <div class="muted" style="padding: 12px 0;">${t("spawn_monitor.no_events")}</div>
      </section>
    `;
  }

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
          ${t("spawn_monitor.events_title")}
          <span style="
            background: rgba(255,255,255,0.08);
            color: var(--text-muted, #888);
            font-size: 11px;
            font-weight: 600;
            padding: 1px 8px;
            border-radius: 10px;
          ">${events.length}</span>
        </div>
        <button class="btn btn--sm" @click=${onClear}>${t("spawn_monitor.clear_events")}</button>
      </div>
      <div class="card-sub">${t("spawn_monitor.events_sub")}</div>

      <div style="margin-top: 12px; max-height: 300px; overflow-y: auto; display: flex; flex-direction: column; gap: 4px;">
        ${events.slice(0, 50).map(
          (evt) => html`
          <div style="
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 12px;
            padding: 6px 10px;
            background: rgba(255,255,255,0.02);
            border-radius: var(--radius-sm, 6px);
          ">
            <span style="color: var(--text-muted, #666); font-family: monospace; font-size: 11px; min-width: 70px;">
              ${formatTime(evt.ts)}
            </span>
            ${
              evt.type === "spawned"
                ? html`
                    <span style="color: var(--accent, #6c8cff)">+</span>
                  `
                : evt.type === "completed"
                  ? html`<span style="color: ${statusColor(evt.status ?? "ok")};">*</span>`
                  : html`
                      <span style="color: var(--text-muted, #888)">~</span>
                    `
            }
            <span style="font-weight: 500;">
              ${evt.type}
            </span>
            <span style="color: var(--text-muted, #aaa); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
              ${evt.label ? evt.label.replace(/^spawn:/, "") : evt.runId.slice(0, 12)}
            </span>
            ${
              evt.runtimeMs != null
                ? html`<span style="margin-left: auto; font-family: monospace; color: var(--text-muted, #888);">${formatMs(evt.runtimeMs)}</span>`
                : nothing
            }
          </div>
        `,
        )}
      </div>
    </section>
  `;
}

export function renderSpawnMonitor(props: SpawnMonitorProps) {
  return html`
    <section class="card" style="margin-bottom: 16px;">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div>
          <div class="card-title">${t("spawn_monitor.title")}</div>
          <div class="card-sub">${t("spawn_monitor.sub")}</div>
        </div>
        <div style="display: flex; gap: 8px; align-items: center;">
          <label style="display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-muted, #aaa); cursor: pointer;">
            <input
              type="checkbox"
              .checked=${props.autoRefresh}
              @change=${props.onToggleAutoRefresh}
              style="accent-color: var(--accent, #6c8cff);"
            />
            ${t("spawn_monitor.auto_refresh")}
          </label>
          <button class="btn btn--sm" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? t("common.loading") : t("common.refresh")}
          </button>
        </div>
      </div>
    </section>

    ${
      props.error
        ? html`<div class="callout callout--error" style="margin-bottom: 16px;">${props.error}</div>`
        : nothing
    }

    ${props.status ? renderStatsCards(props.status) : nothing}

    ${props.status ? renderActiveSpawns(props.status.active) : nothing}

    ${props.status ? renderRecentTable(props.status.recent) : nothing}

    ${renderLiveEvents(props.events, props.onClearEvents)}
  `;
}
